import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Label, Modal } from "reactstrap";
import Uploadicon from "../../../../assets/images/pages/upload-cloud.png";
import CustomeButton from "../../Forms/CustomeButton/CustomeButton";
import { useDispatch } from "react-redux";
import { responseToaster } from "../../../../helperFunctions";
import { reconciliationPost } from "../../../../redux/slices/reconciliationSlice";
import ControlledDropDown from "../../Forms/Controller/ControlledDropDown";
import ControlledDatePicker from "../../Forms/Controller/ControlledDatePicker";
import ControlledInput from "../../Forms/Controller/ControlledInput";




const AddStatementModal = (props) => {
  const { isModalOpen, onCloseModal, bankOptions, onGet } = props;
  const dispatch = useDispatch();
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [mappingData, setMappingData] = useState(null);

  const initialValues = {
    bank_id: "",
    date: null,
    excelFile: null,
    status_col: "",
    amount_col: "",
    utr_col: "",
    date_col: "",
    txn_id_col: "",
    success_values: "",
  };

  const { control, setValue, getValues, handleSubmit, reset, watch, trigger } =
    useForm({
      defaultValues: initialValues,
    });

  const watchExcelFile = watch("excelFile");
  const watchStatusCol = watch("status_col");
  const watchAmountCol = watch("amount_col");
  const watchUtrCol = watch("utr_col");
  const watchDateCol = watch("date_col");
  const watchTxnIdCol = watch("txn_id_col");

  const selectedHeaders = [
    watchStatusCol,
    watchAmountCol,
    watchUtrCol,
    watchDateCol,
    watchTxnIdCol,
  ].filter(Boolean);


  const onClose = () => {
    onCloseModal();
    reset();
    setMappingData(null);
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Stage 1 & Stage 2 submit handling
  const onSubmit = async () => {
    const values = getValues();

    if (!mappingData) {
      // Stage 1: Upload File
      if (!values.bank_id) {
        responseToaster({ status: false, message: "Please select a bank" });
        return;
      }
      if (!values.date) {
        responseToaster({ status: false, message: "Please select a date" });
        return;
      }
      if (!values.excelFile) {
        responseToaster({ status: false, message: "Please upload an excel file" });
        return;
      }

      setIsButtonLoading(true);
      try {
        const fileUpload = new FormData();
        fileUpload.append("bank_id", values.bank_id);
        fileUpload.append("date", formatDateToYYYYMMDD(values.date));
        fileUpload.append("excelFile", values.excelFile);

        const res = await dispatch(reconciliationPost(fileUpload)).unwrap();

        if (res?.status === false && res?.mapped === false) {
          // Headers not mapped, show mapping interface (Stage 2)
          setMappingData(res);
        } else {
          // Success direct upload
          responseToaster(res);
          onClose();
          if (onGet) await onGet();
        }
        setIsButtonLoading(false);
      } catch (err) {
        setIsButtonLoading(false);
      }
    } else {
      // Stage 2: Mapped Values Submit
      if (!values.status_col) {
        responseToaster({ status: false, message: "Please select Status Column" });
        return;
      }
      if (!values.amount_col) {
        responseToaster({ status: false, message: "Please select Amount Column" });
        return;
      }
      if (!values.utr_col) {
        responseToaster({ status: false, message: "Please select UTR Column" });
        return;
      }
      if (!values.date_col) {
        responseToaster({ status: false, message: "Please select Date Column" });
        return;
      }
      if (!values.txn_id_col) {
        responseToaster({ status: false, message: "Please select Txn ID Column" });
        return;
      }
      if (!values.success_values) {
        responseToaster({
          status: false,
          message: (mappingData?.unique_statuses && mappingData.unique_statuses.length > 0)
            ? "Please select Success Value"
            : "Please enter Success Value",
        });
        return;
      }

      setIsButtonLoading(true);
      try {
        const payload = {
          file_path: mappingData.file_path,
          bank_id: values.bank_id,
          date: formatDateToYYYYMMDD(values.date),
          status_col: values.status_col,
          amount_col: values.amount_col,
          utr_col: values.utr_col,
          date_col: values.date_col,
          txn_id_col: values.txn_id_col,
          success_values: values.success_values,
        };

        const res = await dispatch(reconciliationPost(payload)).unwrap();
        responseToaster(res);
        if (res?.status !== false) {
          onClose();
          if (onGet) await onGet();
        }
        setIsButtonLoading(false);
      } catch (err) {
        setIsButtonLoading(false);
      }
    }
  };




  const headerOptions =
    mappingData?.headers?.map((h) => ({ value: h, label: h })) || [];
  const statusOptions =
    mappingData?.unique_statuses?.map((s) => ({ value: s, label: s })) || [];

  return (
    <Modal isOpen={isModalOpen} toggle={onClose} centered={true}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-header">
          <h3 className="modal-title mt-0">
            {mappingData ? "Map Statement Headers" : "Add Statement"}
          </h3>
          <i onClick={onClose} className="fa-solid fa-xmark"></i>
        </div>
        <div className="modal-body">
          {!mappingData ? (
            // Stage 1: File Upload Form
            <>
              <ControlledDropDown
                name="bank_id"
                label="Bank ID"
                placeholder="Select Bank"
                options={bankOptions}
                control={control}
                isDefaultOption={false}
              />
              <div className="mt-1">
                <ControlledDatePicker
                  name="date"
                  placeholder="Select Date"
                  label="Statement Date"
                  maxDate={new Date()}
                  control={control}
                />
              </div>
              <div className="select-div mt-1">
                <Label>Upload File</Label>
                <div className="proof-sec text-start">
                  <label className="label">
                    <input
                      id="excelFile"
                      name="excelFile"
                      type="file"
                      accept=".xlsx"
                      onChange={(e) => {
                        setValue("excelFile", e?.target?.files[0]);
                        trigger("excelFile");
                      }}
                    />
                    <p>
                      <img src={Uploadicon} className="me-1 img-fluid" alt="upload" />
                      <span className={`${watchExcelFile?.name ? "themecolor" : ""}`}>
                        {watchExcelFile?.name || "Upload File (xlsx)"}
                      </span>
                    </p>
                  </label>
                </div>
              </div>
            </>
          ) : (
            // Stage 2: Mapped Column Settings
            <>
              <div className="alert alert-warning py-1 font-size-12">
                {mappingData.message || "Headers are not mapped. Please configure mapping details."}
              </div>
              <div className="row mb-2">
                <div className="col-12 font-size-12 text-muted">
                  <strong>File Path:</strong> {mappingData.file_path}
                </div>
              </div>
              <div className="row g-2">
                <div className="col-12">
                  <ControlledDropDown
                    name="status_col"
                    label="Status Column"
                    placeholder="Select Status Column"
                    options={headerOptions}
                    control={control}
                    isDefaultOption={false}
                    isOptionDisabled={(option) => selectedHeaders.includes(option.value) && option.value !== watchStatusCol}
                  />
                </div>
                <div className="col-12 mt-1">
                  <ControlledDropDown
                    name="amount_col"
                    label="Amount Column"
                    placeholder="Select Amount Column"
                    options={headerOptions}
                    control={control}
                    isDefaultOption={false}
                    isOptionDisabled={(option) => selectedHeaders.includes(option.value) && option.value !== watchAmountCol}
                  />
                </div>
                <div className="col-12 mt-1">
                  <ControlledDropDown
                    name="utr_col"
                    label="UTR Column"
                    placeholder="Select UTR Column"
                    options={headerOptions}
                    control={control}
                    isDefaultOption={false}
                    isOptionDisabled={(option) => selectedHeaders.includes(option.value) && option.value !== watchUtrCol}
                  />
                </div>
                <div className="col-12 mt-1">
                  <ControlledDropDown
                    name="date_col"
                    label="Date Column"
                    placeholder="Select Date Column"
                    options={headerOptions}
                    control={control}
                    isDefaultOption={false}
                    isOptionDisabled={(option) => selectedHeaders.includes(option.value) && option.value !== watchDateCol}
                  />
                </div>
                <div className="col-12 mt-1">
                  <ControlledDropDown
                    name="txn_id_col"
                    label="Transaction ID Column"
                    placeholder="Select Txn ID Column"
                    options={headerOptions}
                    control={control}
                    isDefaultOption={false}
                    isOptionDisabled={(option) => selectedHeaders.includes(option.value) && option.value !== watchTxnIdCol}
                  />
                </div>
                <div className="col-12 mt-1">
                  {statusOptions.length > 0 ? (
                    <ControlledDropDown
                      name="success_values"
                      label="Success Value"
                      placeholder="Select Success Value"
                      options={statusOptions}
                      control={control}
                      isDefaultOption={false}
                    />
                  ) : (
                    <ControlledInput
                      name="success_values"
                      label="Success Value"
                      placeholder="Enter Success Value"
                      control={control}
                    />
                  )}
                </div>

              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <div className="d-flex justify-content-end button-space">
            {mappingData ? (
              <Button
                type="button"
                className="graybutton"
                onClick={() => setMappingData(null)}
              >
                Back
              </Button>
            ) : (
              <Button type="button" className="graybutton" onClick={onClose}>
                Cancel
              </Button>
            )}
            <CustomeButton
              type="submit"
              isButtonLoading={isButtonLoading}
              className="submitbutton"
            >
              {mappingData ? "Submit Mapping" : "Upload"}
            </CustomeButton>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default AddStatementModal;
